//////////////////////////////////////////////////////////////////////////////
// Satellite Orbits Demo: demonstrates the use of the GIS Object to position 
// satellites by specifying lat/lon coordinates and elevation above the Earth.
// The GLG Map Server is used to display a globe in the orthographic 
// projection.
//
// The demo is written in pure HTML5 and JavaScript. The source code of the
// demo uses the GLG Toolkit JavaScript Library supplied by the included
// Glg*.js and GlgToolkit*.js files. The GLG library loads a GLG drawing
// and renders it on a web page, providing an API to handle user interaction
// with graphical objects in the drawing.
//
// The drawings are created using the GLG Graphics Builder, an interactive
// editor that allows to create grahical objects and define their dynamic
// behavior without any programming. The airplane icons are dynamic
// GLG objects created with the Graphics Builder.
//
// The background map is generated by the GLG Map Server, which is integrated
// inside of a GLG drawing as a GLG GIS Object. The GIS Object takes a
// complete care of the map display, automatically generating Map Server
// requests to re-generate the image when the map is panned or zoomed.
//
// By default, demo uses an asyncronous map loading request: when the globe is
// rotated, the program will continue to operate with the current globe view
// and will switch to the new globe view when the new map is ready.
// Alternatively, the USE_REQUEST variable in the code below may be set to false
// to change satellite orbits to match the new globe view right away when the
// globe is rotated, but no map will be displayed while the new map is being
// loaded.
//
// Except for the changes to comply with the JavaScript syntax, this source
// is identical to the source code of the corresponding C/C++/C# and Java
// desktop versions of the demo.
//
// This demo uses the GLG Map Server to display a map.
// The Map Server has to be installed either on the local host or on a
// remote web server. After the Map Server has been installed, modify the
// source code to set the SuppliedMapServerURL variable to point to the
// Map Server location.
//////////////////////////////////////////////////////////////////////////////

/* eslint eqeqeq: 0 */

import { GlgToolkit } from '../GlgToolkitDemo.mod.js'

// Enable general debugging/diagnostics information.
const DEBUG = false;

/* Debugging: set the variable to true to throw an exception on a GLG error
   instead of just displaying an error message on the console.
*/
const DEBUG_GLG_ERRORS = false;

/* This demo uses the GLG Map Server to display a map.
   The Map Server has to be installed either on the local host or on a
   remote web server. After the Map Server has been installed, set the 
   SuppliedMapServerURL variable to point to the Map Server location:

   For example on Linux:
     SuppliedMapServerURL = "http://localhost/cgi-bin/GlmScript";
   or
     SuppliedMapServerURL = "http://www.myserver.com/cgi-bin/GlmScript";

   On Windows:
     SuppliedMapServerURL = "http://localhost/Scripts/GlmScript.pl";
   or
     SuppliedMapServerURL = "http://www.myserver.com/Scripts/GlmScript.pl";

   The SuppliedMapServerURL value overrides the URL of the GIS object in the
   drawing.
*/
const SuppliedMapServerURL = null;    /* String */

/* Set to true to use asynchronous map requests to load the new map: when the
   globe is rotated, the program will continue to operate with the current 
   globe view and will switch to the new globe view when the new map is ready.         
   If set to false, the program will change the satellite orbits to match the
   new globe view right away when the globe is rotated, but no map will be 
   displayed while the new map is being loaded.
*/
const USE_REQUEST = true;

const UPDATE_INTERVAL = 30;   // Update interval in msec
const ANGLE_DELTA = 10.0;     // Defines pan distance.

// Simulation parameters. 
const SAT1_PERIOD = 500;
const SAT2_PERIOD = 400;

// Global handle to the GLG Toolkit library.
let GLG = new GlgToolkit();

//////////////////////////////////////////////////////////////////////////////
// Creates an instance of the satellite demo.
// Parameters:
//   glg_div_name  - name of parent div the drawing will be displayed in,
//                   will be passed by the caller.
//   is_mobile     - true if deployed on mobile devices.
//   is_standalone - true if deployed in html, false if deployed in react or
//                   angular.
//   glg_path      - path to the directory where GLG drawings are located.
//////////////////////////////////////////////////////////////////////////////
export function GlgSatellite( glg_div_name, glg_path, is_standalone, is_mobile )
{
   Debug( "New script for " + glg_div_name + ": "  +
          Object.getPrototypeOf( this ).constructor.name +
          " glg_path: " + glg_path );
    
   if( DEBUG_GLG_ERRORS )   
     GLG.ThrowExceptionOnError( true, true, true );
   else
     GLG.ThrowExceptionOnError( false, false, false );

   this.GLG_div_name = glg_div_name;
   this.IsMobile = is_mobile;
   this.Standalone = is_standalone;    

   // Use path to the drawings directory is supplied.
   this.GlgPath = glg_path;

   // Top level viewport of the loaded drawing.
   this.Drawing = null;                 /* GlgObject */
   this.GISObject = null;               /* GlgObject */
   this.GISArray = null;                /* GlgObject */
   this.Satellite1 = null;              /* GlgObject */
   this.Satellite2 = null;              /* GlgObject */
   this.Satellite1_GroundTrack = null;  /* GlgObject */
   this.Satellite2_GroundTrack = null;  /* GlgObject */
   this.Orbit1 = null;                  /* GlgObject */
   this.Orbit2 = null;                  /* GlgObject */

   this.NumOrbitPoints = 0;      /* int */
   this.UpdateCount = 0;         /* int */

   this.MapLoaded = false;       /* boolean */
   this.InitialLoad = true;      /* boolean */

   // Temporary variables: allocate once.
   this.Position1 = GLG.CreateGlgPoint( 0, 0, 0 );   /* GlgPoint */
   this.Position2 = GLG.CreateGlgPoint( 0, 0, 0 );   /* GlgPoint */

   this.gis_request_observer = null;
    
   /* Coefficient for canvas resolution. It will be adjusted in 
      SetCanvasResolution() for mobile devices with HiDPI displays as well as 
      on browser zoom.
   */
   this.CoordScale = 1;
}

////////////////////////////////////////////////////////////////////////////// 
// Script entry point: Starts satellite demo by loading its drawing.
////////////////////////////////////////////////////////////////////////////// 
GlgSatellite.prototype.Start = function()
{
   Debug( "Starting: " + this.GLG_div_name );

   this.Active = true;
   
   // Set initial size of the drawing.
   this.SetDrawingSize( false );

   /* Load a drawing from the specified drawing file. 
      The LoadCB callback will be invoked when the drawing has been loaded.

      Using "bind( this )" as a shorter way to provide "this" compared with 
      using lambda: with bind, we do not need to specify parameter list that
      we would need to provide for lambda.
   */
   GLG.LoadWidgetFromURL( this.GetFullName( "satellite.g" ), null,
                          this.LoadCB.bind( this ), /*user data*/ null,
                          /*abort test function*/ ()=>!this.Active );
}

////////////////////////////////////////////////////////////////////////////// 
GlgSatellite.prototype.GetFullName = function( drawing_name )
{
   if( this.GlgPath == null )
     return drawing_name;

   return this.GlgPath + "/" + drawing_name;
}

////////////////////////////////////////////////////////////////////////////// 
// Script entry point: Performs cleanup.
////////////////////////////////////////////////////////////////////////////// 
GlgSatellite.prototype.Cleanup = function()
{
   Debug( "Cleanup for: " + this.GLG_div_name );
   
   this.Active = false;    // Ignore any pending updates and callbacks.

   if( this.Drawing )
     this.Drawing.ResetHierarchy();   // Undisplay GLG drawing.

   if( this.ResizeListener )
     window.removeEventListener( "resize", this.ResizeListener );
}

//////////////////////////////////////////////////////////////////////////////
GlgSatellite.prototype.LoadCB = function( drawing, data, path )
{
   Debug( "LoadCB for: " + this.GLG_div_name );

   if( !this.Active )
     return;

   if( drawing == null )
   {
      AppAlert( "Can't load drawing, check console message for details." );
      return;
   }

   if( !document.getElementById( this.GLG_div_name ) )
   {
      Debug( "Can't find " + this.GLG_div_name +
             " div: it may have been removed from the document." );
      return;
   }
    
   // Disable spinning loader.   
   RemoveElement( this.GLG_div_name, "loader_container" );
    
   // Define the element in the HTML page to display the drawing.
   drawing.SetParentElement( this.GLG_div_name );
    
   // Disable viewport border to use the border of the glg_area.
   if( this.Standalone )
     drawing.SetDResource( "LineWidth", 0 );
    
   this.StartSatelliteDemo( drawing );
}

//////////////////////////////////////////////////////////////////////////////
GlgSatellite.prototype.StartSatelliteDemo = function ( drawing )
{
   this.Drawing = drawing;
   
   this.InitDrawing();
   
   this.Drawing.SetupHierarchy();

   this.Drawing.Update();

   // Start animation.
   setTimeout( ()=>this.UpdatePosition(), UPDATE_INTERVAL );
}

//////////////////////////////////////////////////////////////////////////////
// Initializes satellite icons and orbits.
//////////////////////////////////////////////////////////////////////////////
GlgSatellite.prototype.InitDrawing = function()
{
   // Get ID of the GIS object.
   this.GISObject = this.Drawing.GetResourceObject( "GISObject" );   
   
   if( SuppliedMapServerURL != null )
     // Override the URL defined in the drawing.
     this.GISObject.SetSResource( "GISMapServerURL", SuppliedMapServerURL );

   /* Get ID of the GIS Object's GISArray, which holds all icons displayed
      on top of the map in lat/lon coordinates.
   */
   this.GISArray = this.GISObject.GetResourceObject( "GISArray" );
   
   // Get IDs of satellite icons.
   this.Satellite1 = this.GISArray.GetResourceObject( "Satellite1" );
   this.Satellite2 = this.GISArray.GetResourceObject( "Satellite2" );

   this.Satellite1_GroundTrack =
     this.GISArray.GetResourceObject( "Satellite1_GroundTrack" );
   this.Satellite2_GroundTrack =
     this.GISArray.GetResourceObject( "Satellite2_GroundTrack" );

   /* Get orbit templates and remove them from the drawing initially.
      The templates are polygons with 2 points that store graphical
      attributes of the orbit polygons, to allow user to define the
      attributes interactively via the Graphics Builder. 
      Alternatively, the orbit polygons can be created programmatically.
   */
   this.Orbit1 = this.Drawing.GetResourceObject( "Orbit1" );
   this.Orbit2 = this.Drawing.GetResourceObject( "Orbit2" );
   this.Drawing.DeleteThisObject( this.Orbit1 );
   this.Drawing.DeleteThisObject( this.Orbit2 );   

   // Increase sliders size for smaller mobile screens.
   if( this.IsMobile )
     this.Drawing.SetDResource( "SliderScale", 2. );
       
   /* Set the GIS Zoom mode. It was set and saved with the drawing, 
      but do it again programmatically just in case.
   */
   this.Drawing.SetZoomMode( null, this.GISObject, null,
                             GLG.GlgZoomMode.GIS_ZOOM_MODE );

   if( this.IsMobile )
     this.Drawing.SetSResource( "UsagePrompt/String", "To rotate the globe,\n" +
                                "touch and drag or use directional buttons." );
   
   /* Add Input and Trace callbacks used to handle user interaction. */
   this.Drawing.AddListener( GLG.GlgCallbackType.INPUT_CB,
                             this.InputCallback.bind( this ) );
   this.Drawing.AddListener( GLG.GlgCallbackType.TRACE_CB,
                             this.TraceCallback.bind( this ) );

   if( USE_REQUEST )
     this.gis_request_observer =
       GLG.CreateGlgGISRequestObserver( this.RequestUpdate.bind( this ),
                                        this.RequestAdjustment.bind( this ),
                                        this.AdjustRequest.bind( this ) );
   this.SetMapLoading( true, null );
}

//////////////////////////////////////////////////////////////////////////
// Handle user interaction.
//////////////////////////////////////////////////////////////////////////
GlgSatellite.prototype.InputCallback =
  function( /* GlgObject */ vp, /* GlgObject */ message_obj )
{
   if( !this.Active )
     return;

   let origin = message_obj.GetSResource( "Origin" );
   let format = message_obj.GetSResource( "Format" );
   let action = message_obj.GetSResource( "Action" );
   let subaction = message_obj.GetSResource( "SubAction" );

   if( format == "Button" )         // Handle button clicks
   {
      if( action != "Activate" )
        return;
      
      let gis_center = this.GISObject.GetGResource( "GISCenter" );  /*GlgPoint*/
      let lon = gis_center.x;   /* double */
      let lat = gis_center.y;   /* double */
      
      /* Rotate the globe when a directional button is pressed. */
      if( USE_REQUEST )
      {
         let center_flag = GLG.GlgGISRequestFlags.GIS_REQUEST_CENTER;
         this.SetMapLoading( true, null );
         
         /* Request a new map via an asynchronous map request. The program
            will continue to operate with the current globe view and will 
            switch to the new globe view when the new map is received and 
            installed via the InstallGISRequest() method.
         */
         if( origin == "Up" )
           this.GISObject.RequestGISMap( null, 0, 0, lon, lat + ANGLE_DELTA,
                                         0, 0, null, center_flag,
                                         this.gis_request_observer );
         else if( origin == "Down" ) 
           this.GISObject.RequestGISMap( null, 0, 0, lon, lat - ANGLE_DELTA,
                                         0, 0, null, center_flag,
                                         this.gis_request_observer );
         else if( origin == "Left" ) 
           this.GISObject.RequestGISMap( null, 0, 0, lon - ANGLE_DELTA, lat,
                                         0, 0, null, center_flag,
                                         this.gis_request_observer );
         else if( origin == "Right" ) 
           this.GISObject.RequestGISMap( null, 0, 0, lon + ANGLE_DELTA, lat,
                                         0, 0, null, center_flag,
                                         this.gis_request_observer );
         else
         {
            this.SetMapLoading( false, null );
            return;
         }
      }
      else
      {
         /* Set the GIS object's GISCenter directly. The satellite orbits 
            will change to match the new globe view right away, but no map
            will be displayed while the new map is being loaded.
         */
         this.SetMapLoading( true, null );
         if( origin == "Up" ) 
           this.GISObject.SetGResource( "GISCenter",
                                        lon, lat + ANGLE_DELTA, 0.0 );
         else if( origin == "Down" ) 
           this.GISObject.SetGResource( "GISCenter",
                                        lon, lat - ANGLE_DELTA, 0.0 );
         else if( origin == "Left" ) 
           this.GISObject.SetGResource( "GISCenter",
                                        lon - ANGLE_DELTA, lat, 0.0 );
         else if( origin == "Right" ) 
           this.GISObject.SetGResource( "GISCenter",
                                        lon + ANGLE_DELTA, lat, 0.0 );
         else
         {
            this.SetMapLoading( false, null );
            return;
         }
      }
        
      this.Drawing.Update();
   }
   else if( action == "Pan" )
   {
      if( subaction == "Drag" )    // Dragging
      {
         if( !USE_REQUEST && this.MapLoaded )
           this.SetMapLoading( true, "Loading map..." );
      }      
      // Dragging ended or aborted (right mouse button, etc.). 
      else if( subaction == "End" || subaction == "Abort" )
      {
         if( subaction != "Abort" )
           this.SetMapLoading( true, "Loading map..." );
         else
           // Reset message when dragging ends.
           this.SetMapLoading( false, null );

         this.Drawing.Update();
      }
   }
}

//////////////////////////////////////////////////////////////////////////
// Is used to start globe rotation on a mouse click.
//////////////////////////////////////////////////////////////////////////
GlgSatellite.prototype.TraceCallback =
  function( /* GlgObject */ viewport, /* GlgTraceData */ trace_info )
{      
   if( !this.Active )
     return;

   let event_type = trace_info.event_type;

   /* Start dragging only on a mouse click in the Drawing area itself,
      not the sliders.
   */
   if( !trace_info.viewport.Equals( this.Drawing ) )
     return;

   switch( event_type )
   {
    case GLG.GlgEventType.TOUCH_START:
      GLG.SetTouchMode();        /* Start dragging via touch events. */
      /* Fall through */

    case GLG.GlgEventType.MOUSE_PRESSED:
      if( trace_info.button == 1 )             /* Start dragging */
      {
         if( USE_REQUEST )
         {
            this.Drawing.RequestGISZoom( null, 's', 0.0,
                                         this.gis_request_observer );
            this.SetMapLoading( true,
                                "Release the mouse\nto show the new map." );
         }
         else
           this.Drawing.SetZoom( null, 's', 0.0 );
      }
      break;
      
    default: return;
   }      
}

//////////////////////////////////////////////////////////////////////////
// Updates moving objects with new position data.
//////////////////////////////////////////////////////////////////////////
GlgSatellite.prototype.UpdatePosition = function()
{
   if( !this.Active )
     return;
   
   /* If UseRequest=true, the map loading message will be erased when the 
      request is installed.
   */
   if( ( this.InitialLoad || ( !USE_REQUEST && !this.MapLoaded ) ) &&
       Math.trunc( this.GISObject.GetDResource( "ImageLoaded" ) ) != 0 )
   {
      this.SetMapLoading( false, null );
      this.InitialLoad = false;
   }

   // Obtain new positions for both satellites.
   this.GetSatellitePosition( 0, this.Position1 );
   this.GetSatellitePosition( 1, this.Position2 );
   
   // Update satellite positions in the drawing with new data.
   this.Satellite1.SetGResourceFromPoint( "Position", this.Position1 );
   this.Satellite2.SetGResourceFromPoint( "Position", this.Position2 );

   this.Satellite1_GroundTrack.SetGResource( "Position", this.Position1.x,
                                             this.Position1.y, 0.0 );
   this.Satellite2_GroundTrack.SetGResource( "Position", this.Position2.x,
                                             this.Position2.y, 0.0 );
   
   /* Update satellite trajectories. */
   if( this.NumOrbitPoints <= SAT1_PERIOD ||
       this.NumOrbitPoints <= SAT2_PERIOD )
   {
      if( this.NumOrbitPoints == 0 )
      {
         /* First time: add orbit polygons to the GIS Object. 
            Use two points initially, set both points to the same lat/lon.
         */
         SetPolygonPoint( this.Orbit1, 0, this.Position1 );
         SetPolygonPoint( this.Orbit1, 1, this.Position1 );
         
         // Add to top to draw orbits first, with satellites on top of them.
         this.GISArray.AddObjectToTop( this.Orbit1 );
         
         SetPolygonPoint( this.Orbit2, 0, this.Position2 ); 
         SetPolygonPoint( this.Orbit2, 1, this.Position2 ); 
         this.GISArray.AddObjectToTop( this.Orbit2 );

         // Reorder ground tracks to be under the orbit polygons.
         this.GISArray.DeleteThisObject( this.Satellite1_GroundTrack );
         this.GISArray.AddObjectToTop( this.Satellite1_GroundTrack );

         this.GISArray.DeleteThisObject( this.Satellite2_GroundTrack );
         this.GISArray.AddObjectToTop( this.Satellite2_GroundTrack );
      }
      else if( this.NumOrbitPoints == 1 )
      {
         // Set the second point.
         SetPolygonPoint( this.Orbit1, 1, this.Position1 );
         SetPolygonPoint( this.Orbit2, 1, this.Position2 );
      }
      else
      {
         // Add one more point.
         if( this.NumOrbitPoints <= SAT1_PERIOD )
           AddPolygonPoint( this.Orbit1, this.Position1, true );
         
         if( this.NumOrbitPoints <= SAT2_PERIOD )
           AddPolygonPoint( this.Orbit2, this.Position2, true );
      }
      
      ++this.NumOrbitPoints; 
   } 
   ++this.UpdateCount;
   this.Drawing.Update();
   
   // Restart update timer
   setTimeout( ()=>this.UpdatePosition(), UPDATE_INTERVAL );
}

//////////////////////////////////////////////////////////////////////////////
// SIMULATION ONLY: Calculates satellite postion (lon, lat and  elevation
// above the Earth in meters) and returns it in the passed position parameter.
//////////////////////////////////////////////////////////////////////////////
GlgSatellite.prototype.GetSatellitePosition =
  function( /* int */ satellite_index, /* GlgPoint */ position )
{
   let rel_value;   /* double */

   switch( satellite_index )
   {
    case 0:
      rel_value = ( this.UpdateCount % SAT1_PERIOD ) / SAT1_PERIOD;
      position.x = -180.0 + 360.0 * rel_value;
      position.y = 20.0 * Math.sin( 2.0 * Math.PI * rel_value );
      position.z = 1000000.0;
      break;
      
    default:
    case 1:
      rel_value = ( ( 100 + this.UpdateCount ) % SAT2_PERIOD ) / SAT2_PERIOD;
      position.x = 0.0 + 360.0 * rel_value;
      position.y = 20.0 * Math.sin( 2.0 * Math.PI * rel_value );
      position.z = 1000000.0;
      break;
   }
}

//////////////////////////////////////////////////////////////////////////
function SetPolygonPoint( /* GlgObject */ polygon, /* int */ point_index,
                          /* GlgPoint */ position )
{
   let point = polygon.GetElement( point_index );   /* GlgObject */
   point.SetGResourceFromPoint( null, position );
}

//////////////////////////////////////////////////////////////////////////
function AddPolygonPoint( /* GlgObject */ polygon, /* GlgPoint */ position,
                          /* boolean */ add_at_the_end )
{
   let point;  /* GlgObject */

   // Copy the first point and add it at the end of the polygon.
   point = polygon.GetElement( 0 );
   point = point.CopyObject();

   point.SetGResourceFromPoint( null, position );
   if( add_at_the_end )
     polygon.AddObjectToBottom( point );
   else                // add at the beginning of the polygon.
     polygon.AddObjectToTop( point );
}

//////////////////////////////////////////////////////////////////////////
GlgSatellite.prototype.SetMapLoading =
  function( /* boolean */ show, /* String */ message )
{
   if( show )
   {
      if( message == null )
        message = "Loading map...";

      this.Drawing.SetSResource( "LoadingMessage/String", message );
      this.Drawing.SetDResource( "LoadingMessage/Visibility", 1 );
      this.MapLoaded = false;
   }
   else
   {
      this.Drawing.SetDResource( "LoadingMessage/Visibility", 0 );
      this.MapLoaded = true;
   }
}

//////////////////////////////////////////////////////////////////////////////
// Changes drawing size while maintaining width/height aspect ratio.
//////////////////////////////////////////////////////////////////////////////
GlgSatellite.prototype.SetDrawingSize = function( next_size )
{
   const ASPECT_RATIO = 1 / 1;
   
   const MIN_WIDTH = 400;
   const MAX_WIDTH = 1000;
   const SCROLLBAR_WIDTH = 15;

   let span = document.body.clientWidth - SCROLLBAR_WIDTH;    

   if( this.SizeIndex == undefined )   // First time: initialize.
   {
      this.SizeIndex = 0;

      if( this.IsMobile )
      {
         /* Mobile devices use fixed device-width: disable Change Drawing Size 
            button. If it's not standalone, it is handled outside of this 
            script.
         */
         if( this.Standalone )
           RemoveElement( null, this.GLG_div_name + "_change_size" );
      }
      else   /* Desktop */
      {      
         const small_sizes  = [ 1, 1.5,  2.,   2.5 ];
         const medium_sizes = [ 1, 0.75, 1.25, 1.5 ];
         const large_sizes  = [ 1, 0.6,  1.25, 1.5 ];
         
         if( span < 600 )
           this.SetDrawingSize.size_array = small_sizes;
         else if( span < 800 )
           this.SetDrawingSize.size_array = medium_sizes;
         else
           this.SetDrawingSize.size_array = large_sizes;
         
         this.SetDrawingSize.num_sizes = this.SetDrawingSize.size_array.length;
         
         /* Handle browser zooming. */
         this.ResizeListener = ()=>this.SetDrawingSize( false );
         window.addEventListener( "resize", this.ResizeListener );
      }
   }
   else if( next_size )
   {
      ++this.SizeIndex;
      this.SizeIndex %= this.SetDrawingSize.num_sizes;
   }
    
   let drawing_area = document.getElementById( this.GLG_div_name );
   if( this.IsMobile )
   {
      /* Mobile devices use constant device-width, adjust only the height 
         of the drawing to keep the aspect ratio.
      */
      drawing_area.style.height =
        "" + Math.trunc( drawing_area.clientWidth / ASPECT_RATIO ) + "px";
   }
   else   /* Desktop */
   {
      let start_width;
      if( span < MIN_WIDTH )
        start_width = MIN_WIDTH;
      else if( span > MAX_WIDTH )
        start_width = MAX_WIDTH;
      else
        start_width = span;

      let size_coeff = this.SetDrawingSize.size_array[ this.SizeIndex ];
      let width = Math.trunc( Math.max( start_width * size_coeff, MIN_WIDTH ) );
      drawing_area.style.width = "" + width + "px";
      drawing_area.style.height = 
        "" + Math.trunc( width / ASPECT_RATIO ) + "px";
   }

   // Adjust canvas resolution for mobile devices and browser zoom state.
   if( !next_size )
     this.SetCanvasResolution();
}

//////////////////////////////////////////////////////////////////////////////
// Increases canvas resolution for mobile devices with HiDPI displays and for
// browser zooming. Sets CoordScale global variable.
//////////////////////////////////////////////////////////////////////////////
GlgSatellite.prototype.SetCanvasResolution = function()
{
   let TextScale, PixelOffsetScale, ScreenCoordScale, NativeWidgetTextScale;

   if( this.IsMobile )
   {
      /* CoordScale parameter defines canvas coordinate scaling.
         Values greater than 1 increase canvas resolution and result in 
         sharper rendering. On mobile devices with devicePixelRatio > 1,
         the value of devicePixelRatio may be used for very crisp rendering
         with very thin lines. For pixel ration greater than 2, limit 
         CoordScale to 2 to draw thicker lines.

         CanvasScale > 1 makes text smaller. The TextScale parameter defines
         the text scaling factor used to increase text size.

         The ScreenCoordScale parameter specifies a scaling factor for fixed
         scale viewports that use screen coordinates. If the size of the fixed
         scale viewport (such as a height of a toolbar) is controlled by a pixel
         offset transformation, ScreenCoordScale may be set to the same value as
         PixelOffsetScale to scale the content of the viewport proportionally
         to its height increase.

         The NativeWidgetTextScale parameter defines the scaling factor that is
         used to scale down text in native widgets (such as native buttons, 
         toggles, etc.) to match the scale of the drawing.
      */
      if( window.devicePixelRatio > 2. )
        this.CoordScale = 2.;
      else
        this.CoordScale = window.devicePixelRatio;

      TextScale = 1.75;
      PixelOffsetScale = TextScale;
      ScreenCoordScale = PixelOffsetScale;
      NativeWidgetTextScale = 0.6;
   }
   else   // Desktop
   {
      /* Change canvas resolution to match browser zoom state. */
      this.CoordScale = window.devicePixelRatio;      
      TextScale = window.devicePixelRatio;
      PixelOffsetScale = window.devicePixelRatio;
      ScreenCoordScale = window.devicePixelRatio;
      NativeWidgetTextScale = 1.;    // Don't change native text size.
   }

   GLG.SetCanvasScale( this.CoordScale, TextScale, NativeWidgetTextScale,
                       PixelOffsetScale, ScreenCoordScale );
}

//////////////////////////////////////////////////////////////////////////
function RemoveElement( parent_id, child_id )
{
   let element;
   if( parent_id == null )
     // No parent was provided: remove from document.
     element = document.getElementById( child_id );
   else
   {
      // Parent ID was provided: remove from parent.
      let parent = document.getElementById( parent_id );
      if( parent == null )
        return;
      element = parent.querySelector( "#" + child_id );
   }
   
   if( element != null )
     element.parentNode.removeChild( element );
}

//////////////////////////////////////////////////////////////////////////////
function Debug( message )
{
   if( DEBUG )
     console.log( message );
}

//////////////////////////////////////////////////////////////////////////////
function AppAlert( message )
{
   window.alert( message );
}

//////////////////////////////////////////////////////////////////////////////
function AppLog( message )
{
   console.log( message );
}

//////////////////////////////////////////////////////////////////////////
// GIS request observer interface, requests no adjustement callbacks.
//////////////////////////////////////////////////////////////////////////
GlgSatellite.prototype.RequestAdjustment = function()
{
   return false;
}
//////////////////////////////////////////////////////////////////////////
// GIS request observer interface: adjustement callback.
//////////////////////////////////////////////////////////////////////////
GlgSatellite.prototype.AdjustRequest =
  function( /* GlgGISRequestData */ request_data )
{
   return false;
}

//////////////////////////////////////////////////////////////////////////
// GIS request observer interface: receives status update notifications.
//////////////////////////////////////////////////////////////////////////
GlgSatellite.prototype.RequestUpdate =
  function( /* GlgObject */ gis_object, status )
{
   switch( status )
   {
    case GLG.GlgGISRequestStatus.GIS_REQUEST_READY:
      if( this.GISObject.InstallGISRequest( null ) )
        this.Drawing.Update();
      else
        AppLog( "Failed to install map request." );
      break;
      
    default:
      AppLog( "Aborting previous GIS request." ); 
      break;
   }

   this.SetMapLoading( false );
}
